import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberIdSchema } from '../identity/legacy-identity-id';

// The staff batch read behind Atlas's minor and parental-consent marker: for
// each requested member who is under 18 today, whether their latest parental
// consent decision is granted.
//
// A batch read rather than a field on the member list or the staff appointment
// list. Both of those bodies are deliberately free of date of birth, and both
// are `.strict()` responses that a loaded Atlas bundle would reject on any new
// key. Asking for markers by id keeps both contracts untouched.
//
// The response carries the member id and one derived boolean and nothing else:
// no date of birth, age, state or consent metadata ever reaches the wire
// (ADR-122 Rule 1, ADR-069). Adults, members with no date of birth, deleted
// members and unknown ids are simply absent, so absence means "no marker".

export const MINOR_CONSENT_MARKERS_MAX_IDS = 100;

/**
 * The `memberIds` query value: comma-separated Galapagos member ids. Blank
 * segments and duplicates are dropped before the bound is applied, so a
 * repeated id is answered once and counted once.
 *
 * Each id is checked against the shared member-id grammar, not an RFC-4122
 * uuid: members imported from the legacy estate keep a GUID whose variant
 * nibble RFC-4122 rejects, the member list returns those ids, and refusing one
 * fails the whole batch it rides in.
 */
export const minorConsentMarkerIdsSchema = z
  .string()
  .transform((raw) => [
    ...new Set(
      raw
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0),
    ),
  ])
  .pipe(z.array(memberIdSchema).min(1).max(MINOR_CONSENT_MARKERS_MAX_IDS));

export const minorConsentMarkersQuerySchema = z
  .object({
    memberIds: z.string(),
  })
  .strict();
export type MinorConsentMarkersQuery = z.infer<typeof minorConsentMarkersQuerySchema>;

// Deliberately NOT `.strict()` at any level (ADR-135 Rule 1): Atlas parses this
// response, and a strict item would turn a future additive field into a failed
// parse of the whole body.
export const minorConsentMarkerSchema = z.object({
  memberId: z.string(),
  parentalConsentGranted: z.boolean(),
});
export type MinorConsentMarker = z.infer<typeof minorConsentMarkerSchema>;

export const minorConsentMarkersDataSchema = z.object({
  items: z.array(minorConsentMarkerSchema),
});
export type MinorConsentMarkersData = z.infer<typeof minorConsentMarkersDataSchema>;

export const minorConsentMarkersResponseSchema = apiEnvelope(minorConsentMarkersDataSchema);
export type MinorConsentMarkersResponse = z.infer<typeof minorConsentMarkersResponseSchema>;
